import Papa from 'papaparse'

// The /members/bulk_upload endpoint parses columns positionally, so we must emit
// this exact order. Users never provide the MFA columns or the message column --
// MFA is left blank and personal_message comes from a single batch field in the UI.
const BACKEND_COLUMNS = [
  'name',
  'email',
  'role',
  'password',
  'sms_mfa_enabled',
  'mfa_phone_number',
  'personal_message',
] as const

// The backend special-cases role === 'SME' (-> Expert) and treats everything else
// as a regular Member. It can't create admins in bulk (sponsor_admin is hardcoded
// False), so we reject anything outside this set rather than silently downgrading
// an 'Admin' row to a Member. A blank role defaults to Member.
const MEMBER_ROLE = 'Member'
const VALID_ROLES = new Set(['member', 'sme'])

// We read columns by header name and validate each row below, so a row with the
// wrong number of fields isn't inherently fatal -- only errors that prevent
// parsing the file at all should block the upload.
const NON_FATAL_PARSE_CODES = new Set(['TooManyFields', 'TooFewFields'])

const MAX_ROWS = 500

export type ReshapeResult = { file: File } | { error: string }

/**
 * Reads the user's CSV (clean columns: name, email, role, password in any order),
 * validates required fields, and rebuilds the 7-column positional CSV the backend
 * expects. Role is optional and defaults to Member; 'SME' creates an Expert. The
 * same `personalMessage` is applied to every row.
 */
export async function reshapeBulkUploadCsv(file: File, personalMessage: string): Promise<ReshapeResult> {
  let text: string
  try {
    text = await file.text()
  } catch {
    return { error: 'We couldn’t read that file. Please try again.' }
  }

  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: header => header.trim().toLowerCase(),
  })

  const fatalError = parsed.errors.find(e => !NON_FATAL_PARSE_CODES.has(e.code))
  if (fatalError) {
    // PapaParse rows are 0-based and exclude the header, so the file line is row + 2.
    const where = typeof fatalError.row === 'number' ? ` (row ${fatalError.row + 2})` : ''
    return { error: `We couldn’t parse that file${where}. Make sure it’s a valid CSV exported from the template.` }
  }

  const headers = parsed.meta.fields ?? []
  if (!headers.includes('email')) return { error: 'Your CSV must include an "email" column.' }

  const rows = parsed.data
  if (rows.length === 0) return { error: 'Your CSV has a header row but no data rows.' }
  if (rows.length > MAX_ROWS) {
    return { error: `Your CSV has ${rows.length} rows. Please upload at most ${MAX_ROWS} users at a time.` }
  }

  const message = personalMessage.trim()
  const outRows: string[][] = []
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const email = (row.email ?? '').trim()
    // Header row is line 1, so the first data row is line 2.
    if (!email) return { error: `Row ${i + 2}: email is required.` }

    const role = (row.role ?? '').trim() || MEMBER_ROLE
    if (!VALID_ROLES.has(role.toLowerCase())) {
      return {
        error: `Row ${i + 2}: "${role}" isn't a valid role. Use "Member" or "SME" — Program Managers must be invited individually.`,
      }
    }

    outRows.push([(row.name ?? '').trim(), email, role, (row.password ?? '').trim(), '', '', message])
  }

  const csv = Papa.unparse({ fields: [...BACKEND_COLUMNS], data: outRows })
  return { file: new File([csv], file.name, { type: 'text/csv' }) }
}
